// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Badge, Button, Center, Group, Loader, Paper, Stack, Switch, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Organization } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { NotificationTypeDefinition } from '@polarissleep/shared';
import {
  buildDisabledNotificationsExtension,
  DISABLED_NOTIFICATIONS_SYSTEM,
  getDisabledNotificationKeys,
  getNotificationsByAudienceAndCategory,
  NotificationAudience,
  NotificationCategory,
} from '@polarissleep/shared';
import { IconDeviceFloppy, IconInfoCircle, IconLock } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { getOrganizationId } from '../../utils/tenant';

// Category display order
const CATEGORY_ORDER: NotificationCategory[] = [
  NotificationCategory.ACCOUNT,
  NotificationCategory.SYSTEM,
  NotificationCategory.HSAT,
  NotificationCategory.DME,
  NotificationCategory.PAYMENT,
  NotificationCategory.REMINDER,
  NotificationCategory.TROUBLESHOOTING,
];

function categoryLabel(cat: NotificationCategory): string {
  return cat.charAt(0) + cat.slice(1).toLowerCase();
}

export function NotificationPreferences(): JSX.Element {
  const medplum = useMedplum();

  const [org, setOrg] = useState<Organization | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Track which non-essential keys are currently DISABLED (checked = disabled).
  const [disabledKeys, setDisabledKeys] = useState<Set<string>>(new Set());

  const orgId = getOrganizationId();

  const fetchOrg = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const loaded = await medplum.readResource('Organization', orgId);
      setOrg(loaded);
      setDisabledKeys(getDisabledNotificationKeys(loaded));
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: `Failed to load tenant Organization: ${normalizeErrorString(err)}`,
        color: 'red',
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  }, [medplum, orgId]);

  useEffect(() => {
    fetchOrg().catch(console.error);
  }, [fetchOrg]);

  // Group patient notifications by category
  const byCategory = useMemo<Map<NotificationCategory, NotificationTypeDefinition[]>>(() => {
    const map = new Map<NotificationCategory, NotificationTypeDefinition[]>();
    for (const cat of CATEGORY_ORDER) {
      const defs = getNotificationsByAudienceAndCategory(NotificationAudience.PATIENT, cat);
      if (defs.length > 0) {
        map.set(cat, defs);
      }
    }
    return map;
  }, []);

  const handleToggle = (key: string, essential: boolean) => {
    if (essential) {
      return;
    }
    setDisabledKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!org) {
      return;
    }
    setSaving(true);
    try {
      const newExtension = buildDisabledNotificationsExtension(Array.from(disabledKeys));
      // Replace the prior disabled-notifications parent extension (if any), append the new one.
      const otherExtensions = (org.extension ?? []).filter((e) => e.url !== DISABLED_NOTIFICATIONS_SYSTEM);
      const updated: Organization = {
        ...org,
        extension: [...otherExtensions, newExtension],
      };
      const saved = await medplum.updateResource(updated);
      setOrg(saved);
      setDisabledKeys(getDisabledNotificationKeys(saved));
      notifications.show({
        title: 'Saved',
        message: 'Notification preferences updated.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: `Failed to save: ${normalizeErrorString(err)}`,
        color: 'red',
        autoClose: false,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack gap="md" p="md">
        <PageHeader title="Notification Preferences" />
        <Paper withBorder radius="md" shadow="sm" p="md">
          <Center py="xl">
            <Loader />
          </Center>
        </Paper>
      </Stack>
    );
  }

  if (!orgId) {
    return (
      <Stack gap="md" p="md">
        <PageHeader title="Notification Preferences" />
        <Alert icon={<IconInfoCircle size={16} />} color="orange">
          Tenant Organization ID is not configured (VITE_MEDPLUM_ORGANIZATION_ID). Cannot manage notification
          preferences.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md" p="md">
      <PageHeader
        title="Notification Preferences"
        actions={
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={() => handleSave()}
            loading={saving}
            disabled={!org || saving}
          >
            Save
          </Button>
        }
      />

      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Disabling a type stops new sends. Reminders already scheduled will still be delivered.
      </Alert>

      {Array.from(byCategory.entries()).map(([cat, defs]) => (
        <Paper key={cat} withBorder radius="md" shadow="sm" p="md">
          <Stack gap="sm">
            <Title order={4}>{categoryLabel(cat)}</Title>
            {defs.map((def) => {
              const isEssential = !!def.essential;
              const isDisabled = disabledKeys.has(def.key);
              const switchEl = (
                <Switch
                  key={def.key}
                  checked={isDisabled}
                  disabled={isEssential}
                  onChange={() => handleToggle(def.key, isEssential)}
                  label={
                    <Group gap="xs" wrap="nowrap">
                      <Stack gap={2}>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>
                            {def.label}
                          </Text>
                          {isEssential && <IconLock size={14} color="var(--mantine-color-dimmed)" />}
                          <Badge size="xs" variant="outline" color="gray">
                            {def.category.toLowerCase()}
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {def.key}
                        </Text>
                      </Stack>
                    </Group>
                  }
                  styles={{
                    label: { cursor: isEssential ? 'default' : 'pointer' },
                    track: { cursor: isEssential ? 'default' : 'pointer' },
                  }}
                  aria-label={`Disable ${def.label}`}
                />
              );
              if (isEssential) {
                return (
                  <Tooltip key={def.key} label="Required notification - cannot be disabled" position="right" withArrow>
                    <div>{switchEl}</div>
                  </Tooltip>
                );
              }
              return switchEl;
            })}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
